import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import type { Phone } from '../../../../redux/reducers/phones'
import { formatPhoneDisplay, phoneTelHref } from '../../../../utils/phone'
import styles from '../ReportModal.module.css'

const DROPDOWN_GAP = 5
const DROPDOWN_MIN_WIDTH = 180

const phoneSubtitle = (phone: Phone) => {
  const parts = [phone.name, phone.job].filter(Boolean)
  return parts.join(', ')
}

type DropdownPosition = {
  top: number
  left: number
}

function computeDropdownPosition(
  trigger: HTMLElement,
  dropdownEl: HTMLElement | null
): DropdownPosition {
  const rect = trigger.getBoundingClientRect()
  const left = Math.max(
    8,
    Math.min(
      rect.right - DROPDOWN_MIN_WIDTH,
      window.innerWidth - DROPDOWN_MIN_WIDTH - 8
    )
  )
  const height = dropdownEl?.offsetHeight ?? 0
  let top = rect.bottom + DROPDOWN_GAP
  if (height > 0 && top + height > window.innerHeight - 8) {
    top = Math.max(8, rect.top - DROPDOWN_GAP - height)
  }
  return { top, left }
}

export const CargoPhoneBlock: FC<{
  dropdownKey: string
  openKey: string | null
  setOpenKey: (k: string | null) => void
  phones?: Phone[]
}> = ({ dropdownKey, openKey, setOpenKey, phones }) => {
  const opened = openKey === dropdownKey
  const resolvedPhones = Array.isArray(phones) ? phones : []
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(
    null
  )

  const updateDropdownPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    setDropdownPosition(
      computeDropdownPosition(trigger, dropdownRef.current)
    )
  }, [])

  useLayoutEffect(() => {
    if (!opened) {
      setDropdownPosition(null)
      return
    }
    updateDropdownPosition()
    const raf = requestAnimationFrame(updateDropdownPosition)
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [opened, resolvedPhones.length, updateDropdownPosition])

  useEffect(() => {
    if (!opened) return

    const closeIfOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (rootRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpenKey(null)
    }

    document.addEventListener('mousedown', closeIfOutside)
    document.addEventListener('touchstart', closeIfOutside)
    return () => {
      document.removeEventListener('mousedown', closeIfOutside)
      document.removeEventListener('touchstart', closeIfOutside)
    }
  }, [opened, setOpenKey])

  const dropdownMenu =
    opened
      ? createPortal(
          <div
            ref={dropdownRef}
            className={styles.cargoPhoneDropdownPortal}
            style={{
              top: dropdownPosition?.top ?? -9999,
              left: dropdownPosition?.left ?? -9999,
              minWidth: DROPDOWN_MIN_WIDTH,
              visibility: dropdownPosition ? 'visible' : 'hidden',
            }}
          >
            {resolvedPhones.map((phone) => {
              const sub = phoneSubtitle(phone)
              return (
                <a
                  key={phone.id}
                  href={`tel:${phoneTelHref(phone.number)}`}
                  className={styles.nowrap}
                >
                  <i
                    className={classNames(
                      styles.icon,
                      styles.cargoIcon16,
                      styles.cargoIconPhone,
                      'mr-1'
                    )}
                  />{' '}
                  {formatPhoneDisplay(phone.number)}
                  <br />
                  {sub ? (
                    <span
                      className={classNames(
                        styles.cargoColorBlack,
                        styles.small3,
                        'pl-4'
                      )}
                    >
                      {sub}
                    </span>
                  ) : null}
                </a>
              )
            })}
          </div>,
          document.body
        )
      : null

  if (resolvedPhones.length === 0) {
    return (
      <div
        className={classNames(
          styles.dropdown,
          styles.dropdownLeft,
          styles.phoneBlockEmptyDesktop
        )}
      >
        <span
          className={classNames(
            styles.btn,
            styles.btnBlueOutline,
            styles.btnIcon,
            styles.btnSmall2,
            styles.btnDropdownMobile,
            styles.btnDropdown
          )}
          aria-hidden
        >
          <i className={classNames(styles.icon, styles.cargoIconPhone)} />
        </span>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      className={classNames(styles.dropdown, styles.dropdownLeft, {
        [styles.opened]: opened,
      })}
    >
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className={classNames(
          styles.btn,
          styles.btnBlueOutline,
          styles.btnIcon,
          styles.btnSmall2,
          styles.btnDropdownMobile,
          styles.btnDropdown
        )}
        onClick={() => setOpenKey(opened ? null : dropdownKey)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpenKey(opened ? null : dropdownKey)
          }
        }}
      >
        <i className={classNames(styles.icon, styles.cargoIconPhone)} />
      </div>
      {dropdownMenu}
    </div>
  )
}
